import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { renderSettingsResourceLabel } from "../../../utils/helpers/settings";

const columnHelper = createColumnHelper();

export const useColumns = ({
    edit,
    remove,
    clone,
    transfer,
    isOpenFromTimesheet = false,
    open
}) => {
    const { t } = useTranslation();

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("plan", {
                header: t("plan"),
                cell: (info) => {
                    const value = info.getValue();
                    if (!value) {
                        return null;
                    }
                    return renderSettingsResourceLabel(value);
                },
            }),
            columnHelper.accessor("bucket.initial", {
                header: t("initial-balance"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),

            columnHelper.accessor("bucket.updates", {
                header: t("added"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
            // Accrued
            columnHelper.accessor("bucket.earned", {
                header: t("accrued"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
            // Pending
            columnHelper.accessor("bucket.pending", {
                header: t("pending"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    const pending = Number(value).toFixed(2);
                    const data = info.row.original;
                    if(!Array.isArray(data.pendingItems) || data.pendingItems.length === 0 ) {
                        return pending;
                    }
                    const id = `pending-bucket-${data.id}`;
                    return <>
                        <span>{pending}</span>
                        
                        <span id={id} className="text-muted text-sm ml-2">
                            <FontAwesomeIcon
                                className="text-primary cursor-pointer px-2  py-1"
                                icon={faInfo}
                                onClick={() => open(data.pendingItems)}
                            />
                        </span>
                    </>
                },
            }),
            //Taken
            columnHelper.accessor("bucket.consumed", {
                header: t("taken"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
            // Available Balance
            columnHelper.accessor("bucket.available", {
                header: t("available-balance"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
        ];
        if (!isOpenFromTimesheet) {
            const actionColumn = columnHelper.display({
                id: "actions",
                enableHiding: false,
                enableColumnFilter: false,
                header: (
                    <div className="text-right">
                        {t("actions")}
                    </div>
                ),
                cell: (info) => {
                    const { id } = info.row.original;
                    return (
                        <div className="actions text-primary text-right">
                            {transfer && 
                                <span
                                    className="btn-inner-icon mr-3 icon-sm cursor-pointer"
                                    onClick={() => transfer(id)}
                                >
                                    <i class="fa-solid fa-right-left" />
                                </span>
                            }
                            {edit && (
                                <span
                                    className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                    onClick={() => edit(id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <i className="fas fa-pen" />
                                </span>
                            )}

                            {remove && (
                                <span
                                    className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                    onClick={() => remove(id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <i className="fas fa-trash" />
                                </span>
                            )}

                            {clone && (
                                <span
                                    className="btn-inner--icon icon-sm cursor-pointer"
                                    onClick={() => clone(id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <i className="ni ni-single-copy-04" />
                                </span>
                            )}
                        </div>
                    );
                },
            });
            columns.push(actionColumn);
        }
        return columns;
    }, [remove, edit, clone, isOpenFromTimesheet, t, open]);
};
